import { z } from 'zod';
import { LandingPagesBuilderSchemaV2 } from './originalSchemas';

/**
 * CTA (Call To Action) schema
 */
export const CTASchema = z.object({
  enabled: z.boolean().optional(),
  text: z.string().optional(),
  link: z.string().optional(),
});

/**
 * Featured Offer Section schema
 */
export const FeaturedOfferSectionSchema = z.object({
  type: z.literal('FeaturedOffer'),
  enabled: z.boolean(),
  bgColor: z.string(),
  colorIndex: z.number().optional(),
  offerId: z.number({
    required_error: 'Please select an offer',
    invalid_type_error: 'Please select an offer',
  }),
  headlineText: z.string().min(1, 'Headline text is required'),
  description: z.string().default(''),
  listedItems: z.array(z.string()).optional().default([]),
  coverImage: z.string().min(1, 'Cover image is required'),
  cta1: CTASchema.optional(),
  cta2: CTASchema.optional(),
});

/**
 * Offers Carousel Section schema
 */
export const OffersCarouselSectionSchema = z.object({
  type: z.literal('OffersCarousel'),
  enabled: z.boolean(),
  bgColor: z.string(),
  colorIndex: z.number().optional(),
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional().default(''),
  offerIds: z.array(z.number()).default([]),
});

/**
 * Extended Landing Page Builder schema that includes
 * base sections from common-schemas plus our custom sections
 *
 * We extract the original sections union from LandingPagesBuilderSchemaV2
 * and add our custom sections to it, ensuring ALL validations work.
 */

// Step 1: Extract the array schema for sections
const baseSectionsArraySchema = LandingPagesBuilderSchemaV2.shape.sections;

// Step 2: Extract the union schema (element type of the array)
const baseSectionUnionSchema = baseSectionsArraySchema._def.type;

// Step 3: Get all the union options (all base section schemas)
const baseSectionOptions = baseSectionUnionSchema._def.options;

// Step 4: Flatten nested unions (created by allowDisabled())
// allowDisabled() creates unions like: union([schema, disabledSchema])
// We need to extract both options from these nested unions
const flattenedOptions: z.ZodTypeAny[] = [];
for (const option of baseSectionOptions) {
  if (option._def?.typeName === 'ZodUnion') {
    // This is a nested union from allowDisabled() - extract both options
    flattenedOptions.push(...option._def.options);
  } else {
    // This is a regular schema (like Introduction, HeroFullVideo)
    flattenedOptions.push(option);
  }
}

// Step 5: Create new union with flattened base sections + our custom sections
// Note: flattenedOptions now contains both "enabled" and "disabled" versions
// from allowDisabled(), so validation will work correctly
const ExtendedSectionsUnion = z.union([
  ...flattenedOptions,
  FeaturedOfferSectionSchema,
  OffersCarouselSectionSchema,
] as unknown as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]);

// Step 6: Extend the base schema with our new sections union
export const ExtendedLandingPagesBuilderSchema =
  LandingPagesBuilderSchemaV2.extend({
    sections: z.array(ExtendedSectionsUnion),
  });

export type IFeaturedOfferSection = z.infer<typeof FeaturedOfferSectionSchema>;
export type IOffersCarouselSection = z.infer<
  typeof OffersCarouselSectionSchema
>;
